import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import './Diario.css'

const LETRAS_DIA = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do']
const COMIDAS_BASE = ['desayuno', 'almuerzo', 'merienda', 'cena']

function aISO(d) { return d.toISOString().split('T')[0] }

function lunesDeSemana(fechaISO) {
  const d = new Date(fechaISO + 'T00:00:00')
  const dow = d.getDay() // 0=domingo..6=sábado
  const diff = dow === 0 ? -6 : 1 - dow
  d.setDate(d.getDate() + diff)
  return aISO(d)
}

function sumarDias(fechaISO, n) {
  const d = new Date(fechaISO + 'T00:00:00')
  d.setDate(d.getDate() + n)
  return aISO(d)
}

function diaCompleto(comidasDelDia) {
  return COMIDAS_BASE.every(tipo => {
    const c = (comidasDelDia ?? []).find(x => x.tipo_comida === tipo)
    if (!c) return false
    return (c.descripcion && c.descripcion.trim() !== '') || c.realizada === false
  })
}

function TiraSemanal({ usuario, fecha, onSeleccionarDia, refrescar }) {
  const hoy = aISO(new Date())
  const [lunesSemana, setLunesSemana] = useState(() => lunesDeSemana(fecha))
  const [completos, setCompletos] = useState({})

  useEffect(() => {
    cargarSemana(lunesSemana)
  }, [lunesSemana, refrescar])

  const cargarSemana = async (lunes) => {
    const domingo = sumarDias(lunes, 6)
    const { data } = await supabase
      .from('control_diario')
      .select('fecha, registro_comidas(tipo_comida, descripcion, realizada)')
      .eq('usuario_id', usuario.id)
      .gte('fecha', lunes)
      .lte('fecha', domingo)

    const mapa = {}
    for (const fila of data ?? []) {
      mapa[fila.fecha] = diaCompleto(fila.registro_comidas)
    }
    setCompletos(mapa)
  }

  const lunesSemanaActual = lunesDeSemana(hoy)
  const esSemanaActual = lunesSemana === lunesSemanaActual
  const dias = LETRAS_DIA.map((letra, i) => {
    const f = sumarDias(lunesSemana, i)
    return { fecha: f, letra, numero: new Date(f + 'T00:00:00').getDate(), esFuturo: f > hoy }
  })

  return (
    <div className="diario-tira-semanal">
      <button className="diario-tira-flecha" onClick={() => setLunesSemana(sumarDias(lunesSemana, -7))}>‹</button>
      <div className="diario-tira-dias">
        {dias.map(d => (
          <button
            key={d.fecha}
            className={`diario-tira-dia ${d.fecha === fecha ? 'seleccionado' : ''} ${d.esFuturo ? 'futuro' : ''}`}
            disabled={d.esFuturo}
            onClick={() => onSeleccionarDia(d.fecha)}
          >
            <span className="diario-tira-letra">{d.letra}</span>
            <span className="diario-tira-numero">{d.numero}</span>
            <span className="diario-tira-check">{completos[d.fecha] ? '✓' : ''}</span>
          </button>
        ))}
      </div>
      <button
        className="diario-tira-flecha"
        onClick={() => setLunesSemana(sumarDias(lunesSemana, 7))}
        disabled={esSemanaActual}
        style={{ opacity: esSemanaActual ? 0.3 : 1 }}
      >›</button>
    </div>
  )
}

function FotoComida({ usuarioId, tipoComida, path, onSubida, onQuitar }) {
  const [signedUrl, setSignedUrl] = useState(null)
  const [subiendo, setSubiendo] = useState(false)
  const inputCamara = useRef(null)
  const inputGaleria = useRef(null)

  useEffect(() => {
    if (!path) { setSignedUrl(null); return }
    supabase.storage.from('fotos-comidas').createSignedUrl(path, 300)
      .then(({ data, error }) => { if (!error) setSignedUrl(data.signedUrl) })
  }, [path])

  const subir = async (file) => {
    if (!file) return
    setSubiendo(true)
    const ext = file.name.split('.').pop()
    const nuevoPath = `${usuarioId}/${Date.now()}-${tipoComida}.${ext}`
    const { error } = await supabase.storage.from('fotos-comidas').upload(nuevoPath, file)
    setSubiendo(false)
    if (error) { alert('No se pudo subir la foto: ' + error.message); return }
    onSubida(nuevoPath)
  }

  return (
    <div className="diario-comida-foto">
      {signedUrl ? (
        <div className="diario-comida-foto-preview">
          <img src={signedUrl} alt="Foto de la comida" />
          <button type="button" className="diario-comida-foto-quitar" onClick={onQuitar}>×</button>
        </div>
      ) : (
        <div className="diario-comida-foto-botones">
          <button type="button" className="diario-comida-foto-btn" onClick={() => inputCamara.current?.click()} disabled={subiendo}>📷 Sacar foto</button>
          <button type="button" className="diario-comida-foto-btn" onClick={() => inputGaleria.current?.click()} disabled={subiendo}>🖼 Elegir de galería</button>
        </div>
      )}
      <input ref={inputCamara} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => subir(e.target.files[0])} />
      <input ref={inputGaleria} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => subir(e.target.files[0])} />
    </div>
  )
}

const COMIDA_VACIA = { id: null, horario: '', descripcion: '', realizada: true, foto_url: null }
const COMIDAS_BASE_VACIAS = {
  desayuno: { ...COMIDA_VACIA },
  almuerzo: { ...COMIDA_VACIA },
  merienda: { ...COMIDA_VACIA },
  cena: { ...COMIDA_VACIA }
}
const LITROS_AGUA_CHIPS = [1, 1.5, 2, 2.5, 3]

export default function Diario({ usuario }) {
  const hoy = new Date().toISOString().split('T')[0]
  const [fecha, setFecha] = useState(hoy)
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [guardado, setGuardado] = useState(false)

  const [form, setForm] = useState({
    horas_sueno: '',
    litros_agua: '',
    minutos_ejercicio: '',
    tipo_ejercicio: '',
    notas: ''
  })
  const [controlDiarioId, setControlDiarioId] = useState(null)
  const [comidas, setComidas] = useState(COMIDAS_BASE_VACIAS)
  const [picoteos, setPicoteos] = useState([])
  const [refrescoSemana, setRefrescoSemana] = useState(0)

  useEffect(() => {
    cargarEntrada(fecha)
  }, [fecha])

  const cargarEntrada = async (f) => {
    setLoading(true)
    setGuardado(false)
    const { data } = await supabase
      .from('control_diario')
      .select('*')
      .eq('usuario_id', usuario.id)
      .eq('fecha', f)
      .maybeSingle()

    if (data) {
      setControlDiarioId(data.id)
      setForm({
        horas_sueno: data.horas_sueno ?? '',
        litros_agua: data.litros_agua ?? '',
        minutos_ejercicio: data.minutos_ejercicio ?? '',
        tipo_ejercicio: data.tipo_ejercicio ?? '',
        notas: data.notas ?? ''
      })

      const { data: comidasData } = await supabase
        .from('registro_comidas')
        .select('*')
        .eq('control_diario_id', data.id)

      const base = { desayuno: { ...COMIDA_VACIA }, almuerzo: { ...COMIDA_VACIA }, merienda: { ...COMIDA_VACIA }, cena: { ...COMIDA_VACIA } }
      const picoteosList = []
      for (const c of comidasData ?? []) {
        const item = { id: c.id, horario: c.horario ?? '', descripcion: c.descripcion ?? '', realizada: c.realizada ?? true, foto_url: c.foto_url ?? null }
        if (c.tipo_comida === 'picoteo') picoteosList.push(item)
        else base[c.tipo_comida] = item
      }
      setComidas(base)
      setPicoteos(picoteosList)
    } else {
      setControlDiarioId(null)
      setForm({ horas_sueno: '', litros_agua: '', minutos_ejercicio: '', tipo_ejercicio: '', notas: '' })
      setComidas(COMIDAS_BASE_VACIAS)
      setPicoteos([])
    }
    setLoading(false)
  }

  const set = (campo, valor) => {
    setForm(f => ({ ...f, [campo]: valor }))
    setGuardado(false)
  }

  const setComidaCampo = (tipo, campo, valor) => {
    setComidas(c => ({ ...c, [tipo]: { ...c[tipo], [campo]: valor } }))
    setGuardado(false)
  }

  const agregarPicoteo = () => {
    setPicoteos(p => [...p, { ...COMIDA_VACIA }])
    setGuardado(false)
  }

  const setPicoteoCampo = (index, campo, valor) => {
    setPicoteos(p => p.map((item, i) => i === index ? { ...item, [campo]: valor } : item))
    setGuardado(false)
  }

  const eliminarPicoteo = async (index) => {
    const item = picoteos[index]
    if (item.id) {
      await supabase.from('registro_comidas').delete().eq('id', item.id)
    }
    setPicoteos(p => p.filter((_, i) => i !== index))
    setGuardado(false)
  }

  const guardar = async () => {
    setGuardando(true)
    const payload = {
      usuario_id: usuario.id,
      fecha,
      horas_sueno: form.horas_sueno !== '' ? parseFloat(form.horas_sueno) : null,
      litros_agua: form.litros_agua !== '' ? parseFloat(form.litros_agua) : null,
      minutos_ejercicio: form.minutos_ejercicio !== '' ? parseInt(form.minutos_ejercicio) : null,
      tipo_ejercicio: form.tipo_ejercicio || null,
      notas: form.notas || null
    }
    const { data: cd } = await supabase
      .from('control_diario')
      .upsert(payload, { onConflict: 'usuario_id,fecha' })
      .select()
      .single()

    setControlDiarioId(cd.id)

    for (const tipo of COMIDAS_BASE) {
      const c = comidas[tipo]
      const tieneContenido = c.horario || (c.descripcion && c.descripcion.trim()) || !c.realizada || c.foto_url
      if (c.id) {
        await supabase.from('registro_comidas')
          .update({ horario: c.horario || null, descripcion: c.descripcion || null, realizada: c.realizada, foto_url: c.foto_url || null })
          .eq('id', c.id)
      } else if (tieneContenido) {
        const { data: nueva } = await supabase.from('registro_comidas')
          .insert({ control_diario_id: cd.id, tipo_comida: tipo, horario: c.horario || null, descripcion: c.descripcion || null, realizada: c.realizada, foto_url: c.foto_url || null })
          .select()
          .single()
        setComidas(prev => ({ ...prev, [tipo]: { ...prev[tipo], id: nueva.id } }))
      }
    }

    const picoteosActualizados = []
    for (const p of picoteos) {
      const tieneContenido = p.horario || (p.descripcion && p.descripcion.trim()) || p.foto_url
      if (p.id) {
        await supabase.from('registro_comidas')
          .update({ horario: p.horario || null, descripcion: p.descripcion || null, foto_url: p.foto_url || null })
          .eq('id', p.id)
        picoteosActualizados.push(p)
      } else if (tieneContenido) {
        const { data: nuevo } = await supabase.from('registro_comidas')
          .insert({ control_diario_id: cd.id, tipo_comida: 'picoteo', horario: p.horario || null, descripcion: p.descripcion || null, foto_url: p.foto_url || null })
          .select()
          .single()
        picoteosActualizados.push({ ...p, id: nuevo.id })
      }
    }
    setPicoteos(picoteosActualizados)

    setGuardando(false)
    setGuardado(true)
    setRefrescoSemana(r => r + 1)
  }

  return (
    <div className="diario-page">
      <div className="page-header">
        <h1 className="page-titulo">Registro</h1>
        <p className="page-subtitulo">Registro diario de hábitos</p>
      </div>

      <TiraSemanal usuario={usuario} fecha={fecha} onSeleccionarDia={setFecha} refrescar={refrescoSemana} />

      {loading ? (
        <div className="loading">Cargando...</div>
      ) : (
        <div className="diario-contenido">

          {/* AGUA */}
          <div className="diario-card">
            <div className="diario-card-header">
              <span className="diario-icono">💧</span>
              <p className="diario-card-titulo">Agua</p>
            </div>
            <div className="diario-sueno-row">
              <input
                className="diario-number-input"
                type="number"
                step="0.1"
                min="0"
                placeholder="0"
                value={form.litros_agua}
                onChange={e => set('litros_agua', e.target.value)}
              />
              <span className="diario-unidad">litros</span>
            </div>
            <div className="diario-sueno-chips">
              {LITROS_AGUA_CHIPS.map(l => (
                <button
                  key={l}
                  className={`diario-chip ${Number(form.litros_agua) === l ? 'active' : ''}`}
                  onClick={() => set('litros_agua', l)}
                >{l}L</button>
              ))}
            </div>
          </div>

          {/* COMIDAS */}
          <div className="diario-card">
            <div className="diario-card-header">
              <span className="diario-icono">🍽</span>
              <p className="diario-card-titulo">Comidas</p>
            </div>
            {[
              { campo: 'desayuno', label: 'Desayuno', emoji: '🌅' },
              { campo: 'almuerzo', label: 'Almuerzo', emoji: '☀️' },
              { campo: 'merienda', label: 'Merienda', emoji: '🍵' },
              { campo: 'cena', label: 'Cena', emoji: '🌙' }
            ].map(({ campo, label, emoji }) => (
              <div key={campo} className={`diario-comida-item ${!comidas[campo].realizada ? 'grisado' : ''}`}>
                <span className="diario-comida-emoji">{emoji}</span>
                <div className="diario-comida-input-wrap">
                  <div className="diario-comida-label-row">
                    <p className="diario-comida-label">{label}</p>
                    <input
                      className="diario-comida-hora"
                      type="time"
                      value={comidas[campo].horario}
                      onChange={e => setComidaCampo(campo, 'horario', e.target.value)}
                    />
                  </div>
                  <textarea
                    className="diario-comida-textarea"
                    placeholder={`¿Qué comiste en el ${label.toLowerCase()}?`}
                    rows={2}
                    value={comidas[campo].descripcion}
                    onChange={e => setComidaCampo(campo, 'descripcion', e.target.value)}
                  />
                  <FotoComida
                    usuarioId={usuario.id}
                    tipoComida={campo}
                    path={comidas[campo].foto_url}
                    onSubida={p => setComidaCampo(campo, 'foto_url', p)}
                    onQuitar={() => setComidaCampo(campo, 'foto_url', null)}
                  />
                  <label className="diario-comida-no-realizada">
                    <input
                      type="checkbox"
                      checked={!comidas[campo].realizada}
                      onChange={e => setComidaCampo(campo, 'realizada', !e.target.checked)}
                    />
                    No la realicé
                  </label>
                </div>
              </div>
            ))}

            {picoteos.map((p, i) => (
              <div key={p.id ?? `nuevo-${i}`} className="diario-comida-item">
                <span className="diario-comida-emoji">🍪</span>
                <div className="diario-comida-input-wrap">
                  <div className="diario-comida-label-row">
                    <p className="diario-comida-label">Picoteo</p>
                    <input
                      className="diario-comida-hora"
                      type="time"
                      value={p.horario}
                      onChange={e => setPicoteoCampo(i, 'horario', e.target.value)}
                    />
                    <button className="diario-picoteo-eliminar" onClick={() => eliminarPicoteo(i)}>×</button>
                  </div>
                  <textarea
                    className="diario-comida-textarea"
                    placeholder="¿Qué picoteaste?"
                    rows={2}
                    value={p.descripcion}
                    onChange={e => setPicoteoCampo(i, 'descripcion', e.target.value)}
                  />
                  <FotoComida
                    usuarioId={usuario.id}
                    tipoComida={`picoteo-${i}`}
                    path={p.foto_url}
                    onSubida={val => setPicoteoCampo(i, 'foto_url', val)}
                    onQuitar={() => setPicoteoCampo(i, 'foto_url', null)}
                  />
                </div>
              </div>
            ))}

            <button className="diario-agregar-picoteo" onClick={agregarPicoteo}>+ Agregar picoteo</button>
          </div>

          {/* EJERCICIO */}
          <div className="diario-card">
            <div className="diario-card-header">
              <span className="diario-icono">🏃</span>
              <p className="diario-card-titulo">Ejercicio</p>
            </div>
            <div className="diario-ejercicio-row">
              <input
                className="diario-number-input"
                type="number"
                min="0"
                placeholder="0"
                value={form.minutos_ejercicio}
                onChange={e => set('minutos_ejercicio', e.target.value)}
              />
              <span className="diario-unidad">min</span>
            </div>
            <input
              className="diario-text-input"
              type="text"
              placeholder="Tipo de ejercicio (ej: caminata, yoga, pesas...)"
              value={form.tipo_ejercicio}
              onChange={e => set('tipo_ejercicio', e.target.value)}
            />
          </div>

          {/* SUEÑO */}
          <div className="diario-card">
            <div className="diario-card-header">
              <span className="diario-icono">😴</span>
              <p className="diario-card-titulo">Sueño</p>
            </div>
            <div className="diario-sueno-row">
              <input
                className="diario-number-input"
                type="number"
                step="0.5"
                min="0"
                max="24"
                placeholder="0"
                value={form.horas_sueno}
                onChange={e => set('horas_sueno', e.target.value)}
              />
              <span className="diario-unidad">horas</span>
            </div>
            <div className="diario-sueno-chips">
              {[5, 6, 7, 8, 9].map(h => (
                <button
                  key={h}
                  className={`diario-chip ${Number(form.horas_sueno) === h ? 'active' : ''}`}
                  onClick={() => set('horas_sueno', h)}
                >{h}h</button>
              ))}
            </div>
          </div>

          {/* NOTAS */}
          <div className="diario-card">
            <div className="diario-card-header">
              <span className="diario-icono">📝</span>
              <p className="diario-card-titulo">Notas del día</p>
            </div>
            <textarea
              className="diario-text-input"
              style={{ minHeight: 80 }}
              placeholder="¿Cómo te sentiste hoy? ¿Algo que quieras recordar?"
              value={form.notas}
              onChange={e => set('notas', e.target.value)}
            />
          </div>

          {/* GUARDAR */}
          <button
            className={`diario-btn-guardar ${guardado ? 'guardado' : ''}`}
            onClick={guardar}
            disabled={guardando}
          >
            {guardando ? 'Guardando...' : guardado ? '✓ Guardado' : 'Guardar registro'}
          </button>
        </div>
      )}
    </div>
  )
}
